import { Observable } from 'rxjs';

import { GOOGLE_SIGN_IN_CLIENT_ID } from '@config/config';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { GOOGLE_SIGN_IN_BUTTON_ID } from '@user/constants';

const GOOGLE_AUTH_SDK_URL = 'https://accounts.google.com/gsi/client';

type SuccessCallback = (credential: string) => void;
type ErrorCallback = (error: unknown) => void;

declare global {
  interface Window {
    google?: typeof google;
  }
}
class GoogleSignIn {
  private client?: google.accounts.id.IdConfiguration;
  private token = '';

  // Store callbacks directly
  private onSuccess: SuccessCallback = () => {
    throw new Error('onSuccess must be set');
  };

  private onError: ErrorCallback = () => {
    throw new Error('onError must be set');
  };

  // Bind methods explicitly using arrow functions to avoid loss of 'this'
  public setSuccessCallback = (callback: SuccessCallback): void => {
    this.onSuccess = callback;
  };

  public setErrorCallback = (callback: ErrorCallback): void => {
    this.onError = callback;
  };

  public setToken = (token: string): void => {
    this.token = token;
  };

  loadSDK$(): Observable<void> {
    return new Observable<void>(observer => {
      const enableGoogleSignIn = featureFlags.getFlag('ENABLE_GOOGLE_SIGN_IN');

      if (!enableGoogleSignIn) {
        observer.next();
        observer.complete();
        return;
      }

      // Avoid re-injecting the script if it already exists
      if (document.querySelector(`script[src="${GOOGLE_AUTH_SDK_URL}"]`)) {
        observer.next();
        observer.complete();
        return;
      }

      const script = document.createElement('script');
      script.src = GOOGLE_AUTH_SDK_URL;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        observer.next();
        observer.complete();
      };

      script.onerror = err => {
        observer.error(err);
      };

      document.body.appendChild(script);
    });
  }

  public init(onSuccess: SuccessCallback, onFailure: ErrorCallback): void {
    // 1. First assign callbacks
    this.setErrorCallback(error => {
      console.warn('Google Sign In error', error);
      onFailure(error);
    });

    this.setSuccessCallback(credential => {
      this.setToken(credential);
      onSuccess(credential);
    });

    // 2. Initialize Google client ONLY after callbacks are configured
    if (window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_SIGN_IN_CLIENT_ID as string,
        callback: response => {
          if (typeof this.onSuccess === 'function') {
            this.onSuccess(response.credential);
          } else {
            console.warn('onSuccess is not a function at execution time', this);
          }
        },
        // @ts-expect-error: Error callback
        error_callback: error => {
          if (typeof this.onError === 'function') {
            this.onError(error);
          }
        },
      });

      const button = document.getElementById(GOOGLE_SIGN_IN_BUTTON_ID);
      if (button) {
        window.google.accounts.id.renderButton(button, {
          type: 'standard',
          width: 200,
          text: 'signin_with',
          theme: 'outline',
        });
      }
    }
  }
}

export default new GoogleSignIn();
